import { PhotoDomainService } from './domain';
import { PhotoOrderFactory } from './entity/photoOrderFactory';
import { ChildId } from './entity/valueObjects/childId';
import { NumberOfItems } from './entity/valueObjects/numberOfItems';
import { PhotoId } from './entity/valueObjects/photoId';
import { PhotoItemId } from './entity/valueObjects/photoItemId';
import { SalesGroupId } from './entity/valueObjects/salesGroupId';

import type { PhotoItemIRepository } from './entity/photoItemIRepository';
import type { PhotoOrderIRepository } from './entity/photoOrderIRepository';
import type { PhotoSalesIRepository } from './entity/photoSalesIRepository';
import type { EntityDataPubType } from '@/domain/entityBase';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { EntityDataBaseType } from '@/types/common/entityBase';
import type { PhotoOrderJsonPub, PhotoOrderJsonPvt } from '@/types/domain/photo/entities/photoOrderEntity';

/**
 * 写真アプリケーションサービス
 */
export class PhotoApplicationService {
  private service!: PhotoDomainService;

  private photoOrderFactory = new PhotoOrderFactory();

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param photoItemRepository photoItemRepository
   * @param photoOrderRepository photoOrderRepository
   * @param photoSalesRepository photoSalesRepository
   */
  inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    photoItemRepository: PhotoItemIRepository,
    photoOrderRepository: PhotoOrderIRepository,
    photoSalesRepository: PhotoSalesIRepository,
    logger: Logger,
  ) {
    this.service = new PhotoDomainService(logger);
    this.service.inject(
      wagakoNurseryApi,
      photoItemRepository,
      photoOrderRepository,
      photoSalesRepository,
    );
  }

  /**
   * 写真販売リストを取得
   * @param token トークン
   * @returns 結果
   */
  public async getPhotoSalesList(token: string | null) {
    const result = await this.service.getPhotoSalesList(token);
    const items = result.items.map((v) => v.toJson());
    return {
      /** アイテムリスト */
      items,
      /** 次のトークン */
      nextToken: result.nextToken,
    };
  }

  /**
   * 写真のurlを取得する 購入確定写真
   * @param nurseryId 施設ID
   * @param photoOrder photoOrder
   * @returns NurseryApiSpecialGetPhotoOrdered.PhotoUrlType[] | undefined
   */
  public async getOrderedPhotoUrls(
    nurseryId: string,
    photoOrder: EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub>,
  ) {
    const entity = this.photoOrderFactory.createFromJson(photoOrder);
    const items = await this.service.getOrderedPhotoUrls(nurseryId, entity);
    return items;
  }

  /**
   * 写真のurlリストから画像をダウンロードしてZIPとしてダウンロード
   * @param photoList { url: string }[]
   */
  public async downloadPhotoFiles(
    filename: string,
    photoList: { url: string }[],
    finishedMessage: (message: string, type: 'info' | 'error') => void,
  ) {
    await this.service.downloadPhotoFiles(filename, photoList, finishedMessage);
  }

  /**
   * 画像をダウンロード
   */
  public async downloadPhotoFile(
    filename: string,
    photo: { url: string },
    finishedMessage: (message: string, type: 'info' | 'error') => void,
  ) {
    await this.service.downloadPhotoFile(filename, photo, finishedMessage);
  }

  /**
   * getPhotoItemList
   * @param nurseryId 施設ID
   * @param token トークン
   * @returns 結果
   */
  public async getPhotoItemList(nurseryId: string, token: string | null) {
    const result = await this.service.getPhotoItemList(nurseryId, token);
    const { nextToken } = result;
    const items = result.items.map((v) => v.toJsonData());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * 指定グループの注文一覧を取得
   * @param token トークン
   * @returns 注文一覧
   */
  public async getPhotoOrderList(token: string | null) {
    const { nextToken, items } = await this.service.getPhotoOrderList(token);
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items: items.map((v) => v.toJson()),
    };
  }

  /**
   * 施設払いで払う
   * @param nurseryId 施設ID
   * @param salesGroupId 販売グループID
   * @param childId 子どもID
   * @param price 料金
   * @param photos 写真リスト
   * @returns 結果
   */
  public async photoPayByNursery(
    nurseryId: string,
    salesGroupId: string,
    childId: string,
    price: number,
    photos: {
      /** 写真ID */
      photoId: string,
      /** 写真アイテムID */
      photoItemId: string,
      /** アイテム数 */
      numberOfItems: number,
    }[],
  ) {
    const result = await this.service.photoPayByNursery(
      nurseryId,
      SalesGroupId.create(salesGroupId),
      ChildId.create(childId),
      price,
      photos.map((v) => ({
        photoId: PhotoId.create(v.photoId),
        photoItemId: PhotoItemId.create(v.photoItemId),
        numberOfItems: NumberOfItems.create(v.numberOfItems),
      })),
    );
    return result;
  }

  /**
   * クレジットカードで払う
   * @param nurseryId 施設ID
   * @param salesGroupId 販売グループID
   * @param childId 子どもID
   * @param price 価格
   * @param photos 写真リスト
   * @param cardId カードID
   * @returns 結果
   */
  public async photoPayByCard(
    nurseryId: string,
    salesGroupId: string,
    childId: string,
    price: number,
    photos: {
      /** 写真ID */
      photoId: string,
      /** 写真アイテムID */
      photoItemId: string,
      /** アイテム数 */
      numberOfItems: number,
    }[],
    cardId: string,
  ) {
    const result = await this.service.photoPayByCard(
      nurseryId,
      SalesGroupId.create(salesGroupId),
      ChildId.create(childId),
      price,
      photos.map((v) => ({
        photoId: PhotoId.create(v.photoId),
        photoItemId: PhotoItemId.create(v.photoItemId),
        numberOfItems: NumberOfItems.create(v.numberOfItems),
      })),
      cardId,
    );
    return result;
  }

  /**
   * 注文キャンセル
   * @param nurseryId 施設ID
   * @param order 注文
   * @returns 結果
   */
  public async cancelOrder(
    nurseryId: string,
    order: EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub>,
  ) {
    const entity = this.photoOrderFactory.createFromJson(order);
    const result = await this.service.cancelOrder(
      nurseryId,
      entity,
    );
    return result;
  }

  /**
   * 写真注文リストを更新する(Create)
   * @param photoOrderList ミルクリスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createPhotoOrderStore(
    photoOrderList: EntityDataBaseType<PhotoOrderJsonPvt, PhotoOrderJsonPub>[],
    payload: EntitySubscribeType,
    filter: ((json: PhotoOrderJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      photoOrderList,
      payload,
      this.photoOrderFactory,
      filter,
    );
  }

  /**
   * 写真注文リストを更新する(Update)
   * @param photoOrderList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updatePhotoOrderStore(
    photoOrderList: EntityDataBaseType<PhotoOrderJsonPvt, PhotoOrderJsonPub>[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      photoOrderList,
      payload,
      this.photoOrderFactory,
    );
  }

  /**
   * 写真注文リストを更新する(Delete)
   * @param photoOrderList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deletePhotoOrderStore(
    photoOrderList: EntityDataBaseType<PhotoOrderJsonPvt, PhotoOrderJsonPub>[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      photoOrderList,
      payload,
      this.photoOrderFactory,
    );
  }
}
